/**
 * Created: 2026-09-13
 * Initial commit: Add device management page for app admin
 *
 * IoT device management page showing all devices across all sites.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import AddIcon from '@mui/icons-material/Add'
import RefreshIcon from '@mui/icons-material/Refresh'
import TuneIcon from '@mui/icons-material/Tune'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { useState } from 'react'
import { IotDevicesDocument, type IotDevicesQuery } from '../gql/graphql'
import AddIotDeviceDialog from './AddIotDeviceDialog'
import IotControlPointsDialog from './IotControlPointsDialog'

type IotDevice = IotDevicesQuery['iotDevices'][number]

gql`
  query IotDevices {
    iotDevices {
      deviceId
      site {
        id
        name
      }
      wgIp
      certExpiry
      lastSeen
      createdAt
    }
  }
`

export default function IotDevicesPage() {
  const { data, loading, error, refetch } = useQuery(IotDevicesDocument)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [pointsDevice, setPointsDevice] = useState<IotDevice | null>(null)

  const devices = data?.iotDevices || []

  const formatDateTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return '-'
    return format(new Date(timestamp), 'yyyy-MM-dd HH:mm:ss', { locale: zhCN })
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h4" gutterBottom>
          IoT 设备管理
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton aria-label="刷新" onClick={() => refetch()}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            添加设备
          </Button>
        </Box>
      </Box>

      <AddIotDeviceDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAdded={() => refetch()}
      />

      {pointsDevice ? (
        <IotControlPointsDialog
          open
          onClose={() => setPointsDevice(null)}
          deviceId={pointsDevice.deviceId}
          siteId={pointsDevice.site.id}
          siteName={pointsDevice.site.name}
        />
      ) : null}

      {/* Spinner/error only before the first result; a refetch keeps the
          current rows on screen and swaps in the new data when it arrives */}
      {!data && loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : !data && error ? (
        <Typography color="error" sx={{ mt: 3 }}>
          加载设备列表失败: {error.message}
        </Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>设备 ID</TableCell>
                <TableCell>场馆</TableCell>
                <TableCell>WG IP</TableCell>
                <TableCell>证书过期时间</TableCell>
                <TableCell>最后在线时间</TableCell>
                <TableCell>创建时间</TableCell>
                <TableCell align="center">控制点</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {devices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">暂无设备</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                devices.map((device) => (
                  <TableRow key={device.deviceId}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      >
                        {device.deviceId}
                      </Typography>
                    </TableCell>
                    <TableCell>{device.site.name}</TableCell>
                    <TableCell>
                      {device.wgIp ? (
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                        >
                          {device.wgIp}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{formatDateTime(device.certExpiry)}</TableCell>
                    <TableCell>{formatDateTime(device.lastSeen)}</TableCell>
                    <TableCell>{formatDateTime(device.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="管理控制点">
                        <IconButton
                          aria-label={`管理设备 ${device.deviceId} 的控制点`}
                          onClick={() => setPointsDevice(device)}
                        >
                          <TuneIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
